import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Destination, DestinationFilters } from '../types/Destination';
import { deleteDestination, fetchDestinations, getDestinationFull } from '../api/Destinations';

export const useDestinationsData = (filters: DestinationFilters = {}) => {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchCity, setSearchCity] = useState<string>('');
  const [searchResult, setSearchResult] = useState<Destination | null>(null);
  const [searchError, setSearchError] = useState<string>('');

  const loadDestinations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDestinations(filters);
      setDestinations(Array.isArray(data) ? data : []);
    } catch (error) {
      setDestinations([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDestinations();
  }, [loadDestinations]);

  const handleDelete = useCallback(
    async (slug: string, title: string) => {
      if (!window.confirm(`Delete destination "${title}"?`)) return;
      try {
        await deleteDestination(slug);
        loadDestinations();
        if (searchResult && searchResult.slug === slug) {
          setSearchResult(null);
        }
      } catch (error) {
        toast.error('Failed to delete destination.');
      }
    },
    [loadDestinations, searchResult]
  );

  const handleSearch = useCallback(async (term: string) => {
    const normalizedTerm = term.trim().toLowerCase();
    if (!normalizedTerm) {
      setSearchResult(null);
      setSearchError('');
      return;
    }
    const localMatch = destinations.find(
      (item) => item.slug.toLowerCase() === normalizedTerm || item.title.toLowerCase().includes(normalizedTerm)
    );
    if (localMatch) {
      setSearchResult(localMatch);
      setSearchError('');
      return;
    }
    try {
      const result = await getDestinationFull(normalizedTerm);
      setSearchResult(result);
      setSearchError('');
    } catch (error) {
      setSearchResult(null);
      setSearchError('Destination not found. Search by exact slug or title.');
    }
  }, [destinations]);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchCity(value);
      handleSearch(value);
    },
    [handleSearch]
  );

  return {
    destinations,
    loading,
    searchCity,
    searchResult,
    searchError,
    loadDestinations,
    handleDelete,
    handleSearchInputChange
  };
};
